import React from 'react';
import { isAndroid, isIOS, isMobile } from 'react-device-detect';

// URLs for different platforms
const URLS = {
  ios: 'https://apps.apple.com/ma/app/venez/id6446121281',
  android: 'https://play.google.com/store/apps/details?id=ma.venez.app',
  web: 'https://venez.ma',
  proIos: 'https://apps.apple.com/in/app/venez-pro/id6451050438',
  proAndroid: 'https://play.google.com/store/apps/details?id=ma.venez.pro'
};

// Translations
const translations = {
  en: {
    welcome: 'Welcome to Venez',
    redirecting: 'Redirecting you to the',
    inSeconds: 'in',
    seconds: 'seconds...',
    goNow: 'Go Now',
    ifNotRedirected: 'If you\'re not redirected automatically,',
    clickHere: 'click here',
    iosAppStore: 'iOS App Store',
    googlePlayStore: 'Google Play Store',
    website: 'Venez website'
  },
  fr: {
    welcome: 'Bienvenue sur Venez',
    redirecting: 'Redirection vers',
    inSeconds: 'dans',
    seconds: 'secondes...',
    goNow: 'Aller Maintenant',
    ifNotRedirected: 'Si vous n\'êtes pas redirigé automatiquement,',
    clickHere: 'cliquez ici',
    iosAppStore: 'l\'App Store iOS',
    googlePlayStore: 'Google Play Store',
    website: 'le site web Venez'
  },
  ar: {
    welcome: 'مرحبًا بك في Venez',
    redirecting: 'جاري تحويلك إلى',
    inSeconds: 'في غضون',
    seconds: 'ثوان...',
    goNow: 'اذهب الآن',
    ifNotRedirected: 'إذا لم يتم إعادة توجيهك تلقائيًا،',
    clickHere: 'انقر هنا',
    iosAppStore: 'متجر آب ستور',
    googlePlayStore: 'متجر جوجل بلاي',
    website: 'موقع Venez'
  }
};

interface CSSPropertiesPolyfill {
  [key: string]: string | number;
}

const App = () => {
  const [redirectUrl, setRedirectUrl] = React.useState(null as string | null);
  const [redirecting, setRedirecting] = React.useState(true);
  const [countdown, setCountdown] = React.useState(3);
  const [language, setLanguage] = React.useState('en'); // Default language is English
  
  // Detect user's browser language
  React.useEffect(() => {
    const browserLang = navigator.language.substring(0, 2).toLowerCase();
    if (browserLang === 'fr') {
      setLanguage('fr');
    } else if (browserLang === 'ar') {
      setLanguage('ar');
    } else {
      setLanguage('en');
    }
  }, []);

  React.useEffect(() => {
    // Safe check for window and document objects to avoid SSR issues
    if (typeof window !== 'undefined' && typeof document !== 'undefined') {
      // Check if the path is /pro to determine which set of URLs to use
      const isPro = window.location.pathname.includes('/pro');
      
      // Update page title based on whether it's Pro or not
      document.title = isPro ? 'Venez Pro' : 'Venez';
      
      // Determine the appropriate redirect URL based on the device and whether it's for Pro
      if (isIOS) {
        setRedirectUrl(isPro ? URLS.proIos : URLS.ios);
      } else if (isAndroid) {
        setRedirectUrl(isPro ? URLS.proAndroid : URLS.android);
      } else {
        setRedirectUrl(URLS.web);
      }
    }

    // Start countdown
    const timer = setInterval(() => {
      setCountdown((prev: number) => {
        if (prev <= 1) {
          clearInterval(timer);
          // Perform the redirect after countdown
          if (redirectUrl) {
            window.location.href = redirectUrl;
          }
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [redirectUrl]);

  // Style for the app
  const containerStyle: CSSPropertiesPolyfill = {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    textAlign: language === 'ar' ? 'right' : 'center',
    padding: '20px 20px 40px',
    backgroundColor: '#FFFFFF',
    fontFamily: "'Nunito', sans-serif",
    direction: language === 'ar' ? 'rtl' : 'ltr',
  };

  const headerStyle: CSSPropertiesPolyfill = {
    backgroundColor: '#FF7722',
    width: '100%',
    padding: '20px 0',
    borderRadius: '0 0 15px 15px',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'absolute',
    top: 0,
    left: 0,
    boxShadow: '0 2px 10px rgba(255, 119, 34, 0.3)',
  };

  const logoStyle: CSSPropertiesPolyfill = {
    width: '140px',
    height: '80px',
    margin: '30px 0',
    padding: '5px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '0px',  // Hide text
    backgroundImage: 'url("data:image/svg+xml,%3Csvg width=\'140\' height=\'67\' viewBox=\'0 0 140 67\' fill=\'none\' xmlns=\'http://www.w3.org/2000/svg\' class=\'w-%5B90px%5D xl:w-%5B110px%5D 2xl:w-%5B140px%5D\'%3E%3Cpath d=\'M34.2258 17.7246H29.8462L27.6561 21.5168L25.4686 25.3113L23.279 29.1061L21.0889 32.9007H29.8485L32.036 29.1061L34.2284 25.3113L36.4157 21.5191L38.6055 17.7246H34.2258Z\' fill=\'%2322AAFF\'%3E%3C/path%3E%3Cpath d=\'M18.8997 36.6949L16.7122 32.9001L14.5221 29.1079L12.3325 25.3108L10.145 21.5186L7.95488 17.7241L5.7653 13.9316L3.57754 17.7241L1.3877 21.5186L3.57493 25.3108L5.7653 29.1056L7.95488 32.9001L10.145 36.6949L12.3348 40.4894L14.5221 44.2819L16.7122 48.0764H21.0919L23.2796 44.2842L21.0895 40.4894L18.8997 36.6949Z\' fill=\'url(%23paint0_linear_199_2498)\'%3E%3C/path%3E%3Cpath d=\'M57.5866 36.44C57.5866 37.24 57.5332 37.96 57.4266 38.6H41.2266C41.3599 40.2 41.9199 41.4533 42.9066 42.36C43.8932 43.2667 45.1066 43.72 46.5466 43.72C48.6266 43.72 50.1066 42.8267 50.9866 41.04H57.0266C56.3866 43.1733 55.1599 44.9333 53.3466 46.32C51.5332 47.68 49.3066 48.36 46.6666 48.36C44.5332 48.36 42.6132 47.8933 40.9066 46.96C39.2266 46 37.9066 44.6533 36.9466 42.92C36.0132 41.1867 35.5466 39.1867 35.5466 36.92C35.5466 34.6267 36.0132 32.6133 36.9466 30.88C37.8799 29.1467 39.1866 27.8133 40.8666 26.88C42.5466 25.9467 44.4799 25.48 46.6666 25.48C48.7732 25.48 50.6532 25.9333 52.3066 26.84C53.9866 27.7467 55.2799 29.04 56.1866 30.72C57.1199 32.3733 57.5866 34.28 57.5866 36.44ZM51.7866 34.84C51.7599 33.4 51.2399 32.2533 50.2266 31.4C49.2132 30.52 47.9732 30.08 46.5066 30.08C45.1199 30.08 43.9466 30.5067 42.9866 31.36C42.0532 32.1867 41.4799 33.3467 41.2666 34.84H51.7866ZM73.9541 25.52C76.5941 25.52 78.7274 26.36 80.3541 28.04C81.9807 29.6933 82.7941 32.0133 82.7941 35V48H77.1941V35.76C77.1941 34 76.7541 32.6533 75.8741 31.72C74.9941 30.76 73.7941 30.28 72.2741 30.28C70.7274 30.28 69.5007 30.76 68.5941 31.72C67.7141 32.6533 67.2741 34 67.2741 35.76V48H61.6741V25.84H67.2741V28.6C68.0207 27.64 68.9674 26.8933 70.1141 26.36C71.2874 25.8 72.5674 25.52 73.9541 25.52ZM108.719 36.44C108.719 37.24 108.666 37.96 108.559 38.6H92.3594C92.4927 40.2 93.0527 41.4533 94.0394 42.36C95.026 43.2667 96.2394 43.72 97.6794 43.72C99.7594 43.72 101.239 42.8267 102.119 41.04H108.159C107.519 43.1733 106.293 44.9333 104.479 46.32C102.666 47.68 100.439 48.36 97.7994 48.36C95.666 48.36 93.746 47.8933 92.0394 46.96C90.3594 46 89.0394 44.6533 88.0794 42.92C87.146 41.1867 86.6794 39.1867 86.6794 36.92C86.6794 34.6267 87.146 32.6133 88.0794 30.88C89.0127 29.1467 90.3194 27.8133 91.9994 26.88C93.6794 25.9467 95.6127 25.48 97.7994 25.48C99.906 25.48 101.786 25.9333 103.439 26.84C105.119 27.7467 106.413 29.04 107.319 30.72C108.253 32.3733 108.719 34.28 108.719 36.44ZM102.919 34.84C102.893 33.4 102.373 32.2533 101.359 31.4C100.346 30.52 99.106 30.08 97.6394 30.08C96.2527 30.08 95.0794 30.5067 94.1194 31.36C93.186 32.1867 92.6127 33.3467 92.3994 34.84H102.919ZM117.927 43.4H127.807V48H111.567V43.48L121.247 30.44H111.607V25.84H127.687V30.36L117.927 43.4ZM134.623 48.28C133.609 48.28 132.769 47.9733 132.103 47.36C131.463 46.72 131.143 45.9333 131.143 45C131.143 44.0667 131.463 43.2933 132.103 42.68C132.769 42.04 133.609 41.72 134.623 41.72C135.609 41.72 136.423 42.04 137.063 42.68C137.703 43.2933 138.023 44.0667 138.023 45C138.023 45.9333 137.703 46.72 137.063 47.36C136.423 47.9733 135.609 48.28 134.623 48.28Z\' fill=\'black\'%3E%3C/path%3E%3Cdefs%3E%3ClinearGradient id=\'paint0_linear_199_2498\' x1=\'1.75718\' y1=\'13.9316\' x2=\'24.0359\' y2=\'14.4324\' gradientUnits=\'userSpaceOnUse\'%3E%3Cstop stop-color=\'%23FF7722\'%3E%3C/stop%3E%3Cstop offset=\'1\' stop-color=\'%23FFB800\'%3E%3C/stop%3E%3C/linearGradient%3E%3C/defs%3E%3C/svg%3E")',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'center',
    backgroundSize: 'contain',
  };

  const titleStyle: CSSPropertiesPolyfill = {
    fontSize: '28px',
    fontWeight: 'bold',
    margin: '20px 0 10px',
    color: '#333333',
  };

  const subtitleStyle: CSSPropertiesPolyfill = {
    fontSize: '18px',
    margin: '10px 0 30px',
    color: '#555555',
    maxWidth: '80%',
  };

  const buttonStyle: CSSPropertiesPolyfill = {
    backgroundColor: '#FF7722',
    color: 'white',
    border: 'none',
    borderRadius: '25px',
    padding: '12px 30px',
    fontSize: '18px',
    cursor: 'pointer',
    margin: '10px 0',
    fontWeight: 'bold',
    boxShadow: '0 4px 8px rgba(255, 119, 34, 0.3)',
    transition: 'all 0.2s ease',
  };

  const getDeviceText = () => {
    // Check for isPro inside the function to avoid SSR issues
    let isPro = false;
    if (typeof window !== 'undefined') {
      isPro = window.location.pathname.includes('/pro');
    }
    
    const t = translations[language as keyof typeof translations];
    
    if (isIOS) {
      return isPro ? 'Venez Pro - ' + t.iosAppStore : t.iosAppStore;
    }
    if (isAndroid) {
      return isPro ? 'Venez Pro - ' + t.googlePlayStore : t.googlePlayStore;
    }
    return t.website;
  };

  const handleSkip = () => {
    if (redirectUrl) {
      window.location.href = redirectUrl;
    }
  };

  return (
    <div style={containerStyle}>
      <div style={headerStyle}></div>
      
      <div style={logoStyle} aria-label="Venez Logo"></div>
      
      <h1 style={titleStyle}>
        {typeof window !== 'undefined' && window.location.pathname.includes('/pro') 
          ? translations[language as keyof typeof translations].welcome.replace('Venez', 'Venez Pro')
          : translations[language as keyof typeof translations].welcome}
      </h1>
      
      <p style={subtitleStyle}>
        {translations[language as keyof typeof translations].redirecting} {getDeviceText()} {translations[language as keyof typeof translations].inSeconds} {countdown} {translations[language as keyof typeof translations].seconds}
      </p>
      
      <button 
        style={buttonStyle} 
        onClick={handleSkip}
        onMouseOver={(e) => {
          const target = e.currentTarget;
          target.style.backgroundColor = '#FF6600';
          target.style.transform = 'translateY(-2px)';
        }}
        onMouseOut={(e) => {
          const target = e.currentTarget;
          target.style.backgroundColor = '#FF7722';
          target.style.transform = 'translateY(0)';
        }}
      >
        {translations[language as keyof typeof translations].goNow}
      </button>
      
      {/* Added more space for mobile responsiveness */}
      <div style={{ height: '30px' }}></div>
      
      {redirectUrl && (
        <p style={{ 
          fontSize: '14px', 
          color: '#666', 
          marginTop: '10px',
          marginBottom: '20px',
          width: '100%',
          textAlign: 'center',
          padding: '0 20px'
        } as CSSPropertiesPolyfill}>
          {translations[language as keyof typeof translations].ifNotRedirected}{' '}
          <a 
            href={redirectUrl} 
            style={{ 
              color: '#FF7722', 
              marginLeft: language === 'ar' ? '0' : '5px',
              marginRight: language === 'ar' ? '5px' : '0',
              fontWeight: 'bold'
            } as CSSPropertiesPolyfill}
          >
            {translations[language as keyof typeof translations].clickHere}
          </a>
        </p>
      )}
    </div>
  );
};

export default App;